import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getRecentBlogPosts } from '../data/blogPosts';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiClock } = FiIcons;

const LatestArticles = () => {
  const posts = getRecentBlogPosts(3);

  return (
    <section id="latest-articles" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900">
            Latest Articles
          </h2>
          <p className="mt-3 text-lg text-secondary-600">
            Stay ahead of hydrogen-ready turbine trends with fresh analysis
            from our engineering team.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {posts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:-translate-y-2 hover:shadow-2xl transition-all duration-300"
            >
              <Link to={`/blog/${post.slug}`}>
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={post.featuredImage.url}
                    alt={post.featuredImage.alt}
                    className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="bg-primary-600/90 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {post.category}
                    </span>
                  </div>
                </div>
              </Link>

              <div className="p-5">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                  <div className="flex items-center">
                    <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-1" />
                    <time dateTime={post.publishDate}>
                      {new Date(post.publishDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </time>
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiClock} className="h-4 w-4 mr-1" />
                    <span>{post.readTime}</span>
                  </div>
                </div>

                <Link to={`/blog/${post.slug}`}>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2 line-clamp-2 hover:text-primary-600 transition-colors">
                    {post.title}
                  </h3>
                </Link>

                <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>

                <Link
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center text-primary-600 text-sm font-semibold hover:text-primary-700 transition-colors"
                  aria-label={`Read article: ${post.title}`}
                >
                  Read article
                  <span aria-hidden="true" className="ml-1">→</span>
                </Link>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/blog"
            className="inline-flex items-center px-8 py-3 rounded-lg bg-primary-600 text-white font-semibold shadow-md hover:bg-primary-700 hover:shadow-lg transition-all duration-200"
          >
            View all articles
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LatestArticles;
